import Mustache from 'mustache';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import localizations from './localizations';

/**
 * Search vehicles from an ajax endpoint and render them via mustache
 *
 * @class VehicleSearch
 */
class VehicleSearch {

  constructor(){
    this.vehicleSearchForm = document.getElementById('vehicle-search-form');
    this.vehicleSearchSidebar = document.querySelector('.vehicle-search__sidebar');
    this.cardListWrapper = document.querySelector('.card-list--wrapper');
    this.loadingSpinner = document.getElementById('vehicle-search-spinner');
    this.orderControl = document.querySelector('.js-vehicle-search-sort');
    this.nextPageButton = document.querySelector('.js-vehicle-search-next-page');
    this.prevPageButton = document.querySelector('.js-vehicle-search-prev-page');
    this.mobileResultAmount = document.querySelector('.js-vehicle-search-results-amount');
    this.searchWatcherLink = document.querySelector('.js-get-search-watcher-params');
    this.sortingChips = document.querySelectorAll('.sorting-chip');

    this.seoText = document.querySelector('.seo-ingress');

    this.formTimeOutId = null;

    this.templateVehicleList = document.getElementById('mustache-vehicle-list').innerHTML;
    this.templateVehicleCard = document.getElementById('mustache-vehicle-card').innerHTML;
    this.templateAdvertisementCard = document.getElementById('mustache-advertisement-card').innerHTML;
    this.templateNewVehicleCard = document.getElementById('mustache-new-vehicle-card')?.innerHTML;
    this.templateVehicleListHeading = document.getElementById('mustache-vehicle-list-heading').innerHTML;

  }

  /**
   * Serialize form element into query string
   *
   * @param {*} form form element
   * @return {string} query string
   * @memberof VehicleSearch
   */
  _serializeForm(form) {
    let formData = new FormData(form)
    let queryParams = [];
    for (let pair of formData.entries()) {
      if( pair[0] && pair[1] ) queryParams.push(pair[0] + '=' + pair[1])
    }
    return queryParams.length ? encodeURI('?' + queryParams.join('&')) : '';
  }


  /**
   * Empty vehicle list so new vehicles can be shown
   *
   * @memberof VehicleSearch
   */
  _clearVehicleList() {
    this.loadingSpinner.classList.remove('d-none');
    this.cardListWrapper.innerHTML = '';

    document.getElementById('vehicle-search-seo')?.remove();
  }

  /**
   * Get vehicles from ajax endpoint with selected form filters
   *
   * @param {object} form vehicle search form element
   * @returns {object} request response or rejected promise
   * @memberof VehicleSearch
   */
  _getVehicles(form) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const queryString = this._serializeForm(form);

      history.pushState( { queryString: queryString }, '', window.location.pathname + queryString);

      request.open('GET', '/stage-ajax/getVehicles' + queryString, true);
      request.send();

      request.onload = () => {
        if(request.readyState === 4 && request.status === 200) {
          resolve(request.response);
        } else reject(request)
      }

      request.onerror = () => reject(request);
    });
  }

  /**
   * Render ajax response as html with mustache
   *
   * @param {object} response raw json from ajax
   * @memberof VehicleSearch
   */
  _renderVehicles(response) {
    response = JSON.parse(response);
    this.loadingSpinner.classList.add('d-none');

    const cardListTitle = document.querySelector('.card-list--title');
    let siteTitle = '';
    const single = (response.paging.totalResults === 1) ? 'ajoneuvo' : 'ajoneuvoa';
    siteTitle = ( response.paging.totalResults === 1) ? `Löytyi ${response.paging.totalResults} ${single}` : `Löytyi ${response.paging.totalResults} ${single}`;

    // SET SEO TEXT to DOM
    if (this.seoText) {
      this.seoText.innerHTML = response.seoText;
    }

    const queryParams = new URLSearchParams(window.location.search)

    /*
    * Group params by key
    */
    const searchParams = {};
    queryParams.forEach((value, key) => {

      //check if key contains [] and remove it
      key = key.replace('[]', '');
      if( !searchParams[key] ) searchParams[key] = [];
      searchParams[key].push(value);
    });

    if( searchParams.make && response.items.length ){
      const make =( searchParams.make[0] === 'SKODA' || searchParams.make[0] === 'Skoda') ? 'ŠKODA' : searchParams.make;
      let model = (searchParams.model && searchParams.model.length === 1) ? ' ' + searchParams.model[0] : '';
      siteTitle = make + model + `, löytyi ${response.paging.totalResults} ${single}`;
    }

    cardListTitle.outerHTML = Mustache.render(this.templateVehicleListHeading, { heading: siteTitle, paging: response.paging });

    if(localizations.page_title) document.title = localizations.page_title;

    this.mobileResultAmount.innerHTML = `(${response.paging.totalResults})`;
    this.cardListWrapper.innerHTML = Mustache.render(this.templateVehicleList, response, { vehicleCard: this.templateVehicleCard, advertisementCard: this.templateAdvertisementCard, newVehicleCard: this.templateNewVehicleCard });
  }

  /**
   * Show error message to user
   *
   * @param {*} error error message
   * @memberof VehicleSearch
   */
  _renderError(error) {
    this.loadingSpinner.classList.add('d-none');
    this.cardListWrapper.innerHTML = `<h3 class="error-text">${localizations.general_error}</h3>`
    console.log(error);
  }


  /**
   * Create event related actions for vehicle search elements
   *
   * @memberof VehicleSearch
   */
  initFilters() {
    this.vehicleSearchForm.addEventListener('submit', e => {
      e.preventDefault();

      // synchronize sorting chips and sorting menu after each search request
      this.updateSortingChipsState();

      this._clearVehicleList();

      this._getVehicles(e.target)
        .then(vehicleData => {
          this._renderVehicles(vehicleData);
        })
        .catch(errorMessage => {
          this._renderError(errorMessage)
        })
    });



    //dispatch change event to trigger js for multiselect fields.
    this.vehicleSearchForm.addEventListener('reset', e => {
      setTimeout(() => { //wait a few ms and then trigger change event to make sure that reset event has finished.
        const selectFields = this.vehicleSearchForm.querySelectorAll('select');
        const changeEvent = new Event('change');
        selectFields.forEach(field => {
          const options = field.querySelectorAll('option');
          options.forEach(option => option.selected = false);
          field.dispatchEvent(changeEvent);
        });

        this.vehicleSearchForm.dispatchEvent(changeEvent);
      }, 5);
    });

    // If Sähkö or Hybridi is selected as fuel, show electric vehicle filters
    const fuelSelect = document.querySelector('select[name="fuel[]"]');
    if (fuelSelect) {
      fuelSelect.addEventListener('change', (e) => {

        // Check if Sähkö or Plug-in-hybridi is selected
        const selectedOptions = Array.from(fuelSelect.selectedOptions).map(option => option.text);
        const electricVehicleFilters = document.querySelector('.vehicle-search__ev-filters');

        if (selectedOptions.includes('Sähkö') || selectedOptions.includes('Plug-in-hybridi')) {
          electricVehicleFilters.classList.remove('d-none');
        } else {
          electricVehicleFilters.classList.add('d-none');
        }

      });
    }

    const priceFilterRadios = document.querySelectorAll('input[name="filter_price_by"]');
    const maxMonthlyInstallmentButton = document.getElementById('multiselect-max_monthly_installment');
    const maxTotalPriceButton = document.getElementById('multiselect-max_price');

    const maxMonthlyInstallmentSelect = document.querySelector('select[name="max_monthly_installment"]');
    const maxTotalPriceSelect = document.querySelector('select[name="max_price"]');

    // Function to update the disabled state of the selects
    function updateSelectState(selectedValue) {
      if (selectedValue === 'total') {
        maxTotalPriceButton.disabled = false;
        maxTotalPriceSelect.disabled = false;
        maxMonthlyInstallmentButton.disabled = true;
        maxMonthlyInstallmentSelect.disabled = true;
      } else if (selectedValue === 'monthly_installment') {
        maxMonthlyInstallmentButton.disabled = false;
        maxMonthlyInstallmentSelect.disabled = false;
        maxTotalPriceButton.disabled = true;
        maxTotalPriceSelect.disabled = true;
      }
    }

    // Attach change listeners to the radio buttons
    priceFilterRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const selectedValue = e.target.value;
        updateSelectState(selectedValue);
      });
    });

    // Initialize the state based on the currently selected radio button
    const initialSelectedRadio = document.querySelector('input[name="filter_price_by"]:checked');
    if (initialSelectedRadio) {
      updateSelectState(initialSelectedRadio.value);
    }

    //trigger search after filter change (with buffer).
    this.vehicleSearchForm.addEventListener('change', e => {
      clearTimeout(this.formTimeOutId);
      this.mobileResultAmount.innerHTML = '';

      this.formTimeOutId = setTimeout(() => {
        const event = new Event('submit', { cancelable: true });
        this.vehicleSearchForm.dispatchEvent(event);
      }, 1000);
    });

    //show more filters button
    const showMoreFiltersButton = document.querySelector('.js-vehicle-search-show-more-filters');
    const additionalFiltres = document.querySelector('.vehicle-search__additional-filters');
    if(showMoreFiltersButton && additionalFiltres) {
      showMoreFiltersButton.addEventListener('click', e => {
        additionalFiltres.classList.remove('d-none');
        showMoreFiltersButton.classList.add('d-none');
      })
    }

    //add js to ordering control select input as it's placed outside the html form element
    this.orderControl.addEventListener('change', (e) => {
      const orderInput = this.vehicleSearchForm.querySelector('input[name="order"]');
      orderInput.value = e.target.value;
      const event = new Event('change');
      this.vehicleSearchForm.dispatchEvent(event);
    });

    //hide and show filters for mobile users
    const searchFilterToggle = document.querySelectorAll('.js-toggle-vehicle-search-filters');
    searchFilterToggle.forEach(button => {
      button.addEventListener('click', e => {
        if(this.filtersOpen()) {
          this.closeFilters();
          history.pushState({filterOpen: true }, null, '');
        } else {
          this.openFilters();
          history.pushState({filterOpen: false }, null, '');
        }
      });
    })


    //handle history related state changes.
    window.onpopstate = (e) => {
      if(!e.state) return false;

      //hide filters on mobile on back press.
      if(typeof e.state.filterOpen === 'undefined' && this.filtersOpen()) {
        this.closeFilters();
      }
      if(e.state.filterOpen === true && this.filtersOpen()) {
        this.closeFilters();
      } else if(e.state.filterOpen === false) {
        this.openFilters();
      }

      //handle vehicle search history push
      if(e.state.queryString) {
        window.location.href = window.location.pathname + e.state.queryString
      }
    }

    this.searchWatcherLink.addEventListener('click', e => {
      e.preventDefault();
      const queryString = this._serializeForm(this.vehicleSearchForm);
      window.location = this.searchWatcherLink.href + queryString;
    });

    this.toggleSalesType();
    const salesTypeInputs = this.vehicleSearchForm.querySelectorAll('input[name="sales_type[]"]');

    salesTypeInputs.forEach(input => {
      input.addEventListener('change', e => this.toggleSalesType());
    });
  }

  /**
   * Open filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  openFilters() {
    this.vehicleSearchSidebar.classList.add('open');
    disableBodyScroll(this.vehicleSearchSidebar);
  }

  /**
   * Close filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  closeFilters() {
    this.vehicleSearchSidebar.classList.remove('open');
    enableBodyScroll(this.vehicleSearchSidebar);
  }

  /**
   *
   *
   * @return {boolean} is filter element open or no
   * @memberof VehicleSearch
   */
  filtersOpen() {
    return this.vehicleSearchSidebar.classList.contains('open');
  }

  /**
   * Choose the make input value based on the selected salesType
   *
   * @memberof VehicleSearch
   */
  toggleSalesType() {
    const selectedTypes = new FormData(this.vehicleSearchForm).getAll('sales_type[]');
    const makeSelect = this.vehicleSearchForm.querySelector('select[name="make"]');
    const makeOptions = makeSelect.querySelectorAll('option');
    // don't send an event if skoda is already selected.
    const sendEvent = makeSelect.options[makeSelect.selectedIndex].text.toLocaleLowerCase() !== 'skoda';

    if(Array.isArray(selectedTypes) && selectedTypes.length && !selectedTypes.includes('usedCar')) {
      makeOptions.forEach(option => {
        const isSkoda = (option.value && option.value.toLocaleLowerCase() === 'skoda');
        option.selected = isSkoda;
        makeSelect.classList.add('disabled');
      });
    } else {
      makeSelect.classList.remove('disabled');
    }

    if(sendEvent) {
      const event = new Event('change');
      makeSelect.dispatchEvent(event);
    }
  }

  /**
   * Initialize vehicle search
   *
   * @memberof VehicleSearch
   */
  init() {
    this.initFilters();
    this.initSortingChips();
  }

  /**
   * Initialize sorting chips
   *
   * @memberof VehicleSearch
   */
  initSortingChips() {
    for (const chip of this.sortingChips) {
      chip.addEventListener('click', (event) => {
        const chipValue = event.target.dataset.value;

        this.orderControl.value = (this.orderControl.value === chipValue)
          ? ''
          : chipValue
        ;

        // Trigger order control update
        this.orderControl.dispatchEvent(new Event('change'));

        // Synch sorting chips with order control dropdown
        this.updateSortingChipsState();
      });
    }

    // perform initial synch of sorting chips and order control
    this.updateSortingChipsState();
  }

  /**
   * Synch sorting chips state with order menu state
   *
   * @memberof VehicleSearch
   */
  updateSortingChipsState() {
    const orderInput = this.orderControl.value;
    for (const chip of this.sortingChips) {

      // sorting input empty -> all chips should be inactive
      if (!orderInput) {
        chip.classList.remove('active');
        continue;
      }

      // chip's value matches sorting input state -> chip should be active
      if(orderInput === chip.dataset.value) {
        chip.classList.add('active');
        continue;
      }

      // By default chip should be inactive
      chip.classList.remove('active');
    }
  }
}

export default new VehicleSearch();
